import { ArrowRight, ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { productIcons } from "../../data/productIcons";
import { productLinks, products } from "../../data/products";
import { Brand } from "../ui/Brand";

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const productButton = useRef<HTMLButtonElement>(null);
  const mobileButton = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    function dismiss(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node))
        setProductsOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (productsOpen) {
        setProductsOpen(false);
        productButton.current?.focus();
      }
      if (isOpen) {
        setIsOpen(false);
        mobileButton.current?.focus();
      }
    }
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [productsOpen, isOpen]);

  function closeMenus() {
    setIsOpen(false);
    setProductsOpen(false);
  }

  return (
    <header className="site-nav">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <nav className="nav-inner" aria-label="Primary">
        <Link to="/" aria-label="Stratify home" onClick={closeMenus}>
          <Brand />
        </Link>
        <div className="nav-pill">
          <Link
            to="/#home"
            className={
              location.pathname === "/"
                ? "nav-link nav-link-active"
                : "nav-link"
            }
          >
            Home
          </Link>
          <div className="product-menu" ref={menuRef}>
            <button
              ref={productButton}
              type="button"
              className="nav-link"
              aria-expanded={productsOpen}
              aria-controls="product-menu"
              onClick={() => setProductsOpen(!productsOpen)}
            >
              Products{" "}
              <ChevronDown
                size={13}
                className={productsOpen ? "chevron-open" : ""}
              />
            </button>
            <AnimatePresence>
              {productsOpen && (
                <motion.div
                  id="product-menu"
                  className="product-popover"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                >
                  {products.map((product) => {
                    const Icon = productIcons[product.id];
                    return (
                      <a
                        key={product.id}
                        href={productLinks[product.id]}
                        className="popover-product"
                        onClick={closeMenus}
                      >
                        <span
                          className={`product-icon product-icon-${product.id}`}
                        >
                          <Icon size={20} strokeWidth={1.75} />
                        </span>
                        <span>
                          <strong>{product.name}</strong>
                          <small>{product.summary}</small>
                        </span>
                        <ArrowUpRight size={16} />
                      </a>
                    );
                  })}
                  <Link
                    to="/#products"
                    className="popover-all"
                    onClick={closeMenus}
                  >
                    View all products <ArrowRight size={15} />
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Link to="/#business-chapter" className="nav-link">
            For businesses
          </Link>
          <Link to="/#about" className="nav-link">
            About us
          </Link>
        </div>
        <div className="nav-actions">
          <Link to="/contact" className="nav-contact">
            Contact us
          </Link>
          <a
            href={productLinks.services}
            className="button button-dark nav-start"
          >
            Browse services <ArrowUpRight size={15} />
          </a>
          <button
            ref={mobileButton}
            type="button"
            className="mobile-menu-toggle"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
            onClick={() => {
              setIsOpen(!isOpen);
              setProductsOpen(false);
            }}
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-navigation"
            className="mobile-navigation"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link to="/#home" onClick={closeMenus}>
              Home <ArrowRight size={18} />
            </Link>
            {products.map((product) => (
              <a
                key={product.id}
                href={productLinks[product.id]}
                onClick={closeMenus}
              >
                {product.name}
                <ArrowUpRight size={18} />
              </a>
            ))}
            <Link to="/#about" onClick={closeMenus}>
              About Stratify <ArrowRight size={18} />
            </Link>
            <Link to="/contact" onClick={closeMenus}>
              Contact us <ArrowRight size={18} />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
